import {
  Component,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Store } from '@ngrx/store';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';
import AwsS3, { AwsS3UploadParameters } from '@uppy/aws-s3';
import Uppy, { Meta, UploadResult } from '@uppy/core';
import axios from 'axios';
import { NGXLogger } from 'ngx-logger';
import { Subject, takeUntil } from 'rxjs';
import { UserModel } from '../../../../models/user.model';
import { UtilsService } from '../../../../services/utils.service';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';

@Component({
  selector: 's3-images-upload',
  standalone: true,
  imports: [
    UppyAngularDashboardModule,
    UppyAngularDashboardModalModule,
    UppyAngularDragDropModule,
    UppyAngularProgressBarModule,
    UppyAngularStatusBarModule,
  ],
  templateUrl: './s3-images-upload.component.html',
  styleUrl: './s3-images-upload.component.css',
})
export class S3ImagesUploadComponent implements OnInit, OnDestroy {
  store: Store<AppState> = inject(Store<AppState>);
  private fbAuth: Auth = inject(Auth);
  private utilsService = inject(UtilsService);
  private logger: NGXLogger = inject(NGXLogger);

  label = input<string | undefined>();
  subTitle = input<string | undefined>();
  preSignedUrlEndpoint = input.required<string>();
  apikey = input.required<string>();

  metadata = input<any | undefined>();
  filePath = input<string | undefined>();

  @Output() uploadStatus = new EventEmitter<{
    success: boolean;
    response?: any;
    error?: any;
  }>();

  user: UserModel | null = null;
  groupMetadata: any = null;

  exit$: Subject<void> = new Subject<void>();
  
  uppy: Uppy = new Uppy({
    autoProceed: false,
    restrictions: {
      maxFileSize: 5 * 1024 * 1024, // 5MB in bytes
      maxNumberOfFiles: 10,
      minNumberOfFiles: 1,
      allowedFileTypes: ['image/jpeg', 'image/jpg', 'image/png'],
    },
  });

  dashboardProps = {
    inline: true,
    hideUploadButton: true,
    hideRetryButton: true,
    hidePauseResumeButton: true,
    hideProgressAfterFinish: true,
    showSelectedFiles: true,
    showProgressDetails: true,
    proudlyDisplayPoweredByUppy: false,
    width: '100%',
    height: '250px',
    note: 'Images only (jpg, jpeg, png), up to 5 MB. Up to 10 images are allowed.',
  };

  constructor() {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.store
      .select(groupSelector.getSelectedGroupMetadata)
      .pipe(takeUntil(this.exit$))
      .subscribe((meta) => {
        this.groupMetadata = meta;
      });
  }

  ngOnInit(): void {
    this.uppy.use(AwsS3, {
      limit: 4,
      shouldUseMultipart: false,
      getUploadParameters: this.getSignedUrl.bind(this),
    });

    this.uppy.on('restriction-failed', (file, error) => {
      // 'error' contains the reason (e.g., "Exceeds max file size")
      this.logger.error('Restriction error:', error.message);
      // You could trigger an Angular Material SnackBar or a simple alert here
      this.utilsService.onShowAlert({
        code: 'ERR_FILE_RESTRICTION',
        message: `Cannot add ${file?.name}: ${error.message}`,
        title: 'File Restriction Error',
      });
    });

    this.uppy.on('upload-success', (file, response) => {
      this.logger.debug(`Upload success: ${file?.name}`);
      this.logger.debug(`Response: ${JSON.stringify(response, null, 2)}`);
      // const bucketPathPattern =
      //   /^https?:\/\/([^.]+)\.s3\.[^.]+\.amazonaws\.com\/(.*)$/;
      // const newUrl = response.uploadURL?.replace(
      //   bucketPathPattern,
      //   `https://${this.cloudFrontDomain()}/$2`,
      // );
      // response.uploadURL = newUrl;
      this.uploadStatus.emit({ success: true, response });
    });

    this.uppy.on('upload-error', (file, error) => {
      this.logger.error(`Upload failed: ${file?.name}`, error);
      this.uploadStatus.emit({ success: false, error });
    });

    this.uppy.on('complete', (result) => {
      this.logger.debug('Upload complete! We uploaded:', result.successful);
      this.uppy.clear();
    });
  }

  ngOnDestroy(): void {
    this.uppy.destroy();
    this.exit$.next();
    this.exit$.complete();
  }

  async getSignedUrl(file: any, options: any): Promise<AwsS3UploadParameters> {
    if (this.user?.uid === null) {
      throw new Error('Unsuccessful request', { cause: 'User not found' });
    }

    const fileExtn: string | undefined = file.name?.split('.')?.pop();
    const token: string | undefined =
      await this.fbAuth.currentUser?.getIdToken();

    if (token === undefined) {
      throw new Error('Unsuccessful request', {
        cause: 'Failed to authenticate user',
      });
    }
    try {
      // Map your metadata to x-amz-meta headers
      const metadataHeaders: Record<string, string> = {};
      Object.entries(this.metadata() ?? {}).forEach(([key, value]) => {
        metadataHeaders[`x-amz-meta-${key.toLowerCase()}`] = String(value);
      });

      const response = await axios.get<any>(this.preSignedUrlEndpoint(), {
        params: {
          uid: this.user?.uid,
          filePath: `${this.filePath()}/${file.name}`,
          ...this.metadata(),
        },
        headers: {
          'x-api-key': this.apikey(),
          AuthorizationToken: `Bearer ${token}`,
          ...metadataHeaders,
          'Content-Type': 'application/json',
        },
      });

      if (!(response.status === 200)) {
        throw new Error('Unsuccessful request', { cause: response });
      }

      // Parse the JSON response.
      const data = await response.data;

      return {
        method: 'PUT',
        url: data.url,
        headers: {
          'Content-Type': file.type,
        },
      };
    } catch (error) {
      this.logger.error(`Error getting upload parameters: ${error}`);
      throw error;
    }
  }

  async upload(): Promise<
    UploadResult<Meta, Record<string, never>> | undefined
  > {
    if (this.uppy?.getFiles().length === 0) {
      throw new Error('No files to upload');
    }

    const limitBytes = this.groupMetadata?.storageLimitBytes || (5 * 1024 * 1024 * 1024); // default 5 GB limit
    const usageBytes = this.groupMetadata?.storageUsageBytes || 0;
    const files = this.uppy.getFiles();
    const totalNewBytes = files.reduce((sum, f) => sum + (f.size || 0), 0);

    if (usageBytes + totalNewBytes > limitBytes) {
      const limitGb = (limitBytes / (1024 * 1024 * 1024)).toFixed(1);
      const usageGb = (usageBytes / (1024 * 1024 * 1024)).toFixed(2);
      const newGb = (totalNewBytes / (1024 * 1024 * 1024)).toFixed(2);

      this.utilsService.onShowAlert({
        code: 'ERR_STORAGE_LIMIT_EXCEEDED',
        title: 'Storage Limit Exceeded',
        message: `Uploading these files (${newGb} GB) will exceed your group's subscribed storage limit of ${limitGb} GB (Current usage: ${usageGb} GB). Please upgrade your subscription plan or free up some space before uploading.`,
      });
      throw new Error('Storage limit exceeded');
    }

    try {
      const result = await this.uppy.upload();
      return result;
    } catch (error) {
      this.uploadStatus.emit({ success: false, error });
      this.clearFiles();
      throw error;
    }
  }

  clearFiles() {
    this.uppy.cancelAll(); // cancel in-progress uploads
    const files = this.uppy.getFiles();
    for (const file of files) {
      this.uppy.removeFile(file.id);
    }
  }
}
